import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { translate, loadUi, tx } from "../lib/i18n";

const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export function AppProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest
  const [counts, setCounts] = useState({ cart: 0, wishlist: 0 });
  const [wishIds, setWishIds] = useState([]);
  const [lang, setLangState] = useState(localStorage.getItem("mivo_lang") || "en");
  const [currency, setCurrencyState] = useState(localStorage.getItem("mivo_currency") || "USD");
  const [rates, setRates] = useState({ rates: { USD: 1 } });
  const [motionPref, setMotionPref] = useState(localStorage.getItem("mivo_motion"));
  const [gate, setGate] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [hidden, setHidden] = useState(document.hidden);

  const motion = motionPref || (reducedMotion() ? "off" : "subtle");
  const t = useCallback((k) => translate(lang, k), [lang]);

  const refreshCounts = useCallback(async () => {
    const { data } = await api.get("/counts");
    setCounts(data);
    if (data.authenticated) {
      const w = await api.get("/wishlist");
      setWishIds(w.data.product_ids);
    } else setWishIds([]);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      const local = localStorage.getItem("mivo_lang") || "en";
      if (data.language && data.language !== local) {
        const l = await loadUi(data.language);
        localStorage.setItem("mivo_lang", l);
        setLangState(l);
      } else if (!data.language) api.patch("/auth/preferences", { language: local }).catch(() => {});
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const onExpire = () => { setUser(false); setGate(true); toast.error(tx("Your session has expired. Please sign in again.")); };
    window.addEventListener("mivo:session-expired", onExpire);
    return () => window.removeEventListener("mivo:session-expired", onExpire);
  }, []);

  useEffect(() => {
    window.__mivoAuthed = !!user;
    if (user !== null) refreshCounts().catch(() => {});
  }, [user, refreshCounts]);

  useEffect(() => {
    api.get("/currency").then((r) => setRates(r.data)).catch(() => {});
    const onVis = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = async (l) => {
    l = await loadUi(l);
    localStorage.setItem("mivo_lang", l);
    setLangState(l);
    if (user) api.patch("/auth/preferences", { language: l }).catch(() => {});
  };
  const setCurrency = (c) => {
    localStorage.setItem("mivo_currency", c);
    setCurrencyState(c);
    if (user) api.patch("/auth/preferences", { currency: c }).catch(() => {});
  };
  const setMotion = (m) => {
    localStorage.setItem("mivo_motion", m);
    setMotionPref(m);
  };

  const formatPrice = useCallback(
    (cents) => {
      const rate = rates.rates?.[currency] ?? 1;
      const cur = rates.rates?.[currency] ? currency : "USD";
      return new Intl.NumberFormat(lang === "en" ? "en-US" : lang, { style: "currency", currency: cur }).format((cents / 100) * rate);
    },
    [rates, currency, lang]
  );

  const requireAuth = useCallback(() => {
    if (user) return true;
    setGate(true);
    return false;
  }, [user]);

  const toggleWish = async (productId) => {
    if (!requireAuth()) return null;
    let data;
    try { ({ data } = await api.post(`/wishlist/${productId}`)); } catch { return null; }
    setWishIds((ids) => (data.saved ? [...ids, productId] : ids.filter((i) => i !== productId)));
    setCounts((c) => ({ ...c, wishlist: data.count }));
    return data.saved;
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  const overlayOpen = gate || cartOpen || searchOpen;
  const value = useMemo(
    () => ({
      user, setUser, checkAuth, logout, counts, setCounts, refreshCounts, wishIds, toggleWish, lang, setLang, currency, setCurrency,
      rates, formatPrice, motion, motionExplicit: !!motionPref, setMotion, gate, setGate, cartOpen, setCartOpen, searchOpen, setSearchOpen,
      requireAuth, t, paused: hidden || overlayOpen,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, counts, wishIds, lang, currency, rates, motion, motionPref, gate, cartOpen, searchOpen, hidden, formatPrice, requireAuth, t]
  );
  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}
